import { useCallback } from 'react';
import { useRouter } from 'next/router';

import { authRedirects } from '@zg-rentals/auth-comet';

import { useIsInMobileWebView } from '../../hooks/useIsInMobileWebView';

import { EXTERNAL_ROUTES, ROUTES } from '../../constants/urls';

import { Auth, useAuth } from '../../components/AuthProvider';

import type React from 'react';

const MFA_RETRY_PARAM = 'mr';
const MAX_UNAUTH_RETRIES = 3;

type RedirectHrefFn = (href: string, params: URLSearchParams) => string;

export type AuthCheckWrapperProps = {
  children: React.ReactNode;
  /**
   * Optional custom redirect when unauthenticated. When provided, skips default logic
   * (__DEV__ → featureFlags, mobile native sign-in, signInPage). Use for page-specific returnUrl.
   */
  redirectHref?: RedirectHrefFn;
  /** Forwarded to Auth.Boundary */
  loading: React.ReactNode;
  /** Forwarded to Auth.Boundary */
  redirecting: React.ReactNode;
  /** Forwarded to Auth.Boundary */
  onError?: React.ReactNode | ((reason: string) => React.ReactNode);
  /** Forwarded to Auth.Boundary */
  onUnauthorized?: React.ReactNode | ((user: unknown) => React.ReactNode);
};

/**
 * Wraps children with Auth.Boundary for Next.js app pages. When the user is not authenticated,
 * redirects to a login page. Pass redirectHref to override with a custom URL (e.g. returnUrl only).
 */
const AuthCheckWrapper = ({
  children,
  redirectHref,
  loading,
  redirecting,
  onError,
  onUnauthorized,
}: AuthCheckWrapperProps) => {
  const router = useRouter();

  const isInMobileWebView = useIsInMobileWebView();

  const { user } = useAuth();

  const resolveRedirectHref = useCallback<RedirectHrefFn>(
    (href, params) => {
      if (redirectHref) {
        return redirectHref(href, params);
      }

      if (__DEV__) {
        return `/rental-manager/${ROUTES.FEATURE_FLAGS}`;
      }

      const manifest = authRedirects.zillow;
      const isMfaRequired = user?.isInitialMultiFactorAuthenticationRequired;
      params.delete('mfaStepUp');
      const search = params.size ? `?${params.toString()}` : '';

      if (isInMobileWebView && 'nativeAppSignInPage' in manifest) {
        const redirectTo = manifest.nativeAppSignInPage?.(href, search);
        if (redirectTo) {
          return redirectTo;
        }
      }

      const loginUrl = manifest.signInPage(href, search, EXTERNAL_ROUTES.SIGN_IN);
      return isMfaRequired ? `${loginUrl}${loginUrl.includes('?') ? '&' : '?'}mfaStepUp=true` : loginUrl;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      redirectHref,
      isInMobileWebView,
      router.pathname,
      router.asPath,
      router.basePath,
      user?.isInitialMultiFactorAuthenticationRequired,
    ],
  );

  // If the user returned from login but still needs MFA step-up, Auth.Boundary's
  // ap=x loop guard will short-circuit the redirect and call onUnauthorized instead
  // of redirecting. Retry the MFA step-up redirect up to MAX_UNAUTH_RETRIES
  // times (tracked via mr=<n>), then fall through to onUnauthorized to avoid a true loop.
  const handleUnauthorized = (unauthorizedUser: unknown) => {
    const fallback = () => (typeof onUnauthorized === 'function' ? onUnauthorized(unauthorizedUser) : onUnauthorized);

    if (typeof window === 'undefined' || !user?.isInitialMultiFactorAuthenticationRequired) {
      return fallback();
    }

    const params = new URLSearchParams(window.location.search);
    const attempt = Number(params.get(MFA_RETRY_PARAM) ?? '0');
    if (attempt >= MAX_UNAUTH_RETRIES) {
      return fallback();
    }

    params.set(MFA_RETRY_PARAM, String(attempt + 1));
    params.delete('ap');

    // Assigning window.location.href kicks off the redirect, but the browser
    // hasn't unloaded yet. Return `redirecting` so React renders a loading
    // state in the meantime instead of a blank screen.
    const href = resolveRedirectHref(window.location.href.split('?')[0], params);
    window.location.href = href;
    return redirecting;
  };

  return (
    <Auth.Boundary
      redirectHref={resolveRedirectHref}
      loading={loading}
      redirecting={redirecting}
      onError={onError}
      onUnauthorized={handleUnauthorized}
    >
      {children}
    </Auth.Boundary>
  );
};

export default AuthCheckWrapper;
